import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warehouseAPI } from '../utils/api';
import { Warehouse, MapPin, Plus, Edit2, X, Package, LayoutGrid, ChevronRight, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { Thumb, ImagePicker } from '../components/common/ImageZoom';
import { localName, fmt, apiError } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';

const emptyForm = { name: '', location: '', description: '' };

export default function Warehouses() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [image, setImage] = useState(null);
  const { user, can } = useAuthStore();
  const isAdmin = can('warehouses.manage');

  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [stockModal, setStockModal] = useState(null);
  const [stockData, setStockData] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  const load = () => {
    setLoading(true);
    warehouseAPI.list()
      .then(r => setWarehouses(r.data))
      .catch(() => toast.error(t('error')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setImage(null);
    setShowModal(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({ name: w.name, location: w.location || '', description: w.description || '' });
    setImage(w.image_path || null);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); setForm(emptyForm); };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error(t('name_required'));
    setSaving(true);
    try {
      let id = editing?.id;
      if (editing) await warehouseAPI.update(id, form);
      else id = (await warehouseAPI.create(form)).data.id;
      if (image instanceof File) await warehouseAPI.uploadImage(id, image);
      toast.success(t('saved'));
      closeModal();
      load();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally {
      setSaving(false);
    }
  };

  const openStock = async (w) => {
    setStockModal(w);
    setStockLoading(true);
    setStockData([]);
    try {
      const r = await warehouseAPI.stock(w.id);
      setStockData(r.data);
    } catch {
      toast.error(t('error'));
    } finally {
      setStockLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <SectionHero title={t('warehouses')} subtitle={<>{warehouses.length} {t('warehouses')}</>}>
        {isAdmin && (
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={16} /> {t('add_warehouse')}
          </button>
        )}
      </SectionHero>

      {/* Grid */}
      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : warehouses.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Warehouse size={40} style={{ color: 'var(--text-muted)', margin: '0 auto 12px' }} />
            <p>{t('no_data')}</p>
          </div>
        </div>
      ) : (
        <div className="grid-3">
          {warehouses.map(w => (
            <div key={w.id} className="card" style={{ transition: 'border-color 0.2s', cursor: 'default' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-active)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
              <div className="flex items-start justify-between mb-4">
                {w.image_path ? <Thumb src={w.image_path} size={44} /> : (
                <div className="stat-icon" style={{ background: 'var(--accent-dim)' }}>
                  <LayoutGrid size={18} style={{ color: 'var(--accent)' }} />
                </div>)}
                <div className="flex gap-1">
                  <button className="btn btn-ghost btn-sm btn-icon" title={t('view_stock')} onClick={() => openStock(w)}>
                    <Eye size={14} />
                  </button>
                  {isAdmin && (
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(w)}>
                      <Edit2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              <h3 className="card-title mb-1" style={{ color: 'var(--text-primary)' }}>{w.name}</h3>
              {w.location && (
                <p className="text-xs flex items-center gap-1 mb-2" style={{ color: 'var(--text-secondary)' }}>
                  <MapPin size={11} /> {w.location}
                </p>
              )}
              {w.description && (
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{w.description}</p>
              )}
              <div className="flex gap-3 mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                <span><strong style={{ color: 'var(--text-primary)' }}>{fmt(w.total_rolls)}</strong> {t('rolls')}</span>
                <span><strong style={{ color: 'var(--text-primary)' }}>{fmt(w.total_sqm)}</strong> {t('sqm')}</span>
              </div>
              <div className="divider" style={{ margin: '14px 0 10px' }} />
              <button className="flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => openStock(w)}>
                <Package size={13} /> {t('view_stock')} <ChevronRight size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{editing ? t('edit_warehouse') : t('add_warehouse')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">{t('name')} *</label>
                <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}  />
              </div>
              <div className="form-group">
                <label className="form-label">{t('location')}</label>
                <input className="input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}  />
              </div>
              <ImagePicker label={t('image')} value={image} onChange={setImage} />
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('description')}</label>
                <textarea className="input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}  style={{ minHeight: 72 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('loading') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Modal */}
      {stockModal && (
        <div className="modal-overlay" onClick={() => setStockModal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 className="card-title">{stockModal.name} — {t('stock')}</h2>
                {stockModal.location && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{stockModal.location}</p>}
              </div>
              <button className="btn btn-ghost btn-icon" onClick={() => setStockModal(null)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding: '16px 24px 24px' }}>
              {stockLoading ? (
                <div className="loading-center"><div className="spinner" /></div>
              ) : stockData.length === 0 ? (
                <div className="empty-state" style={{ padding: '32px 0' }}>
                  <Package size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} />
                  <p>{t('no_data')}</p>
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('product')}</th>
                        <th>{t('rolls')}</th>
                        <th>{t('sqm')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockData.map(s => (
                        <tr key={s.finished_good_id}>
                          <td style={{ color: 'var(--text-primary)' }}>{localName(s, lang)}</td>
                          <td>{fmt(s.quantity_rolls)}</td>
                          <td>{fmt(s.quantity_sqm)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
